import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faBell, faBellSlash } from "@fortawesome/free-solid-svg-icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { parseISO } from "date-fns";
import api from "../lib/api.js";
import { smartDate, swissDate, relativeDate } from "../lib/dates.js";
import { STATUS_CONFIG } from "../lib/mapStyles.js";

function ConfidenceMiniChart({ deviceId }) {
  const chartRef   = useRef(null);
  const wrapperRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["confidence-history", deviceId, 24],
    queryFn:  () => api.get(`devices/${deviceId}/confidence_history`, { searchParams: { hours: 24 } }).json(),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.floor(entry.contentRect.width);
      if (w > 0 && chartRef.current?.chart) {
        chartRef.current.chart.setSize(w, 120, false);
      }
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  if (isLoading) return <div className="data text-xs" style={{ color: "var(--color-muted)" }}>Loading chart…</div>;
  if (!data?.history?.length) return null;

  const series = data.history.map((p) => [parseISO(p.timestamp).getTime(), p.confidence]);
  const threshold = data.detect_threshold;
  const windowStart = Date.now() - 24 * 60 * 60 * 1000;

  const options = {
    chart: {
      backgroundColor: "transparent",
      type: "areaspline",
      height: 120,
      width: null,
      marginLeft: 64,
      marginRight: 12,
      style: { fontFamily: "var(--font-mono)" },
    },
    title:   { text: null },
    credits: { enabled: false },
    legend:  { enabled: false },
    tooltip: {
      backgroundColor: "rgba(10,10,15,0.95)",
      borderColor: "var(--color-border)",
      style: { color: "var(--color-white)", fontFamily: "var(--font-mono)", fontSize: "11px" },
      xDateFormat: "%d.%m %H:%M",
      pointFormatter: function () { return "Confidence: <b>" + Math.round(this.y * 100) + "%</b>"; },
    },
    xAxis: {
      type: "datetime",
      min: windowStart,
      max: Date.now(),
      lineColor: "var(--color-border)",
      tickColor: "var(--color-border)",
      dateTimeLabelFormats: { hour: "%H:%M", day: "%d.%m" },
      labels: { style: { color: "var(--color-muted)", fontSize: "9px" } },
    },
    yAxis: {
      visible: true,
      title: { text: "Confidence", style: { color: "var(--color-muted)", fontSize: "9px" } },
      gridLineColor: "rgba(255,255,255,0.05)",
      labels: { style: { color: "var(--color-muted)", fontSize: "9px" }, formatter: function () { return Math.round(this.value * 100) + "%"; } },
      min: 0,
      max: 1,
      tickInterval: 0.25,
      plotLines: [{
        value: threshold,
        color: "var(--color-pink)",
        width: 1.5,
        dashStyle: "Dash",
        zIndex: 5,
        label: {
          text: `${(threshold * 100).toFixed(0)}%`,
          align: "right",
          style: { color: "var(--color-pink)", fontSize: "9px", fontFamily: "var(--font-mono)" },
          x: -4,
          y: -3,
        },
      }],
    },
    series: [{
      data: series,
      color: "#4488ff",
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, "rgba(68,136,255,0.25)"],
          [1, "rgba(68,136,255,0.02)"],
        ],
      },
      lineWidth: 1.5,
      connectNulls: false,
      marker: { enabled: false, states: { hover: { enabled: true, radius: 3 } } },
      threshold: threshold,
      zones: [
        { value: threshold, color: "#4488ff" },
        { color: "#ff3399" },
      ],
    }],
  };

  return (
    <div ref={wrapperRef} style={{ width: "100%" }}>
      <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />
    </div>
  );
}

export default function UserDevicesPage() {
  const qc = useQueryClient();

  const { data: mapData, isLoading } = useQuery({
    queryKey: ["map-devices"],
    queryFn: () => api.get("map/devices").json(),
    refetchInterval: 30_000,
  });

  const devices = [...(mapData?.devices || [])].sort((a, b) => a.name.localeCompare(b.name));

  const toggleMutation = useMutation({
    mutationFn: (deviceId) => api.patch(`devices/${deviceId}/toggle_notifications`).json(),
    onSuccess: () => qc.invalidateQueries(["map-devices"]),
  });

  if (isLoading) {
    return <div className="p-6 data text-sm" style={{ color: "var(--color-muted)" }}>Loading…</div>;
  }

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold data" style={{ color: "var(--color-white)" }}>My Devices</h1>
        <p className="data text-xs mt-1" style={{ color: "var(--color-muted)" }}>
          {devices.length} device{devices.length !== 1 ? "s" : ""}
        </p>
      </div>

      {devices.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="data text-sm" style={{ color: "var(--color-muted)" }}>
            No devices assigned. Contact an admin.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map((device) => (
            <div key={device.id} className="card p-4" style={{
              borderColor: device.rave ? "rgba(255,51,153,0.5)" : "rgba(68,136,255,0.25)",
            }}>
              {/* Top row: dot + name + actions */}
              <div className="flex items-center gap-3">
                {/* Status dot */}
                {(() => { const sc = STATUS_CONFIG[device.status] || STATUS_CONFIG.normal; return (
                <div style={{
                  width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                  background: sc.color,
                }} />
                ); })()}

                {/* Name + badge */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="data font-semibold" style={{ color: "var(--color-white)", fontSize: 14 }}>
                      {device.name}
                    </span>
                    {/* Desktop: always show status label */}
                    {(() => { const sc = STATUS_CONFIG[device.status] || STATUS_CONFIG.normal; return (
                    <span className="hidden sm:inline-block data" style={{
                      fontSize: 10, letterSpacing: "0.15em", fontWeight: 700, textTransform: "uppercase",
                      fontFamily: "var(--font-mono)",
                      color: sc.color,
                      border: `1px solid ${sc.border}`,
                      borderRadius: 4, padding: "1px 6px",
                      background: sc.bg,
                    }}>
                      {sc.label}
                    </span>
                    ); })()}
                  </div>
                  {device.location && (
                    <div className="data text-xs mt-0.5 truncate" style={{ color: "var(--color-muted)" }}>
                      {device.location}
                    </div>
                  )}
                  {device.last_seen_at && (
                    <div className="hidden sm:block data text-xs" style={{ color: "var(--color-muted)", opacity: 0.7 }}>
                      {relativeDate(device.last_seen_at)}
                    </div>
                  )}
                </div>

                {/* Actions — hidden on mobile, shown on sm+ */}
                <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleMutation.mutate(device.id)}
                    disabled={toggleMutation.isPending}
                    title={device.notifications_enabled ? "Disable notifications" : "Enable notifications"}
                    style={{
                      background: device.notifications_enabled ? "rgba(68,136,255,0.12)" : "transparent",
                      border: `1px solid ${device.notifications_enabled ? "rgba(68,136,255,0.4)" : "var(--color-border)"}`,
                      borderRadius: 6, padding: "6px 10px", cursor: "pointer",
                      color: device.notifications_enabled ? "var(--color-blue)" : "var(--color-muted)",
                      transition: "all 0.2s",
                    }}
                  >
                    <FontAwesomeIcon icon={device.notifications_enabled ? faBell : faBellSlash} />
                  </button>
                  <Link
                    to={`/devices/${device.id}`}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 10px", borderRadius: 6,
                      border: "1px solid var(--color-border)",
                      color: "var(--color-muted)", textDecoration: "none",
                      fontSize: 12, fontFamily: "var(--font-mono)",
                      transition: "all 0.2s",
                    }}
                  >
                    History <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 10 }} />
                  </Link>
                </div>
              </div>

              {/* Measurements */}
              <div className="flex gap-4 mt-3">
                {[
                  { key: "Conf", label: "Confidence", val: null,
                    extra: device.rave_confidence != null ? `${device.rave_confidence}%` : "—",
                    color: device.rave_confidence != null ? (device.rave_confidence >= device.detection_threshold * 100 ? "var(--color-pink)" : "var(--color-cyan)") : "var(--color-muted)" },
                  { key: "SPL",  label: "SPL",                       val: device.spl },
                  { key: "LEQ",  label: <span>L<sub>EQ</sub></span>, val: device.leq },
                  { key: "Lmax", label: <span>L<sub>max</sub></span>,val: device.lmax },
                ].map(({ key, label, val, extra, color }) => (
                  <div key={key} style={{ minWidth: 44 }}>
                    <div className="data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>{label}</div>
                    <div className="data font-semibold" style={{ fontSize: 14, color: color || "var(--color-white)", lineHeight: 1.2 }}>
                      {extra ?? (val != null ? `${(+val).toFixed(1)} dB` : "—")}
                    </div>
                  </div>
                ))}
              </div>

              {/* Confidence chart */}
              <div className="mt-3">
                <ConfidenceMiniChart deviceId={device.id} />
              </div>

              {/* Mobile-only bottom row: location/time + actions */}
              <div
                className="sm:hidden flex items-center justify-between mt-3 pt-3"
                style={{ borderTop: "1px solid var(--color-border)" }}
              >
                <div className="min-w-0 flex-1">
                  {(() => { const sc = STATUS_CONFIG[device.status] || STATUS_CONFIG.normal; return (
                  <div className="data mb-1" style={{
                    display: "inline-block",
                    fontSize: 10, letterSpacing: "0.15em", fontWeight: 700, textTransform: "uppercase",
                    fontFamily: "var(--font-mono)",
                    color: sc.color,
                    border: `1px solid ${sc.border}`,
                    borderRadius: 4, padding: "1px 6px",
                    background: sc.bg,
                  }}>
                    {sc.label}
                  </div>
                  ); })()}
                  {device.last_seen_at && (
                    <div className="data text-xs" style={{ color: "var(--color-muted)", opacity: 0.7 }}>
                      {relativeDate(device.last_seen_at)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <button
                    onClick={() => toggleMutation.mutate(device.id)}
                    disabled={toggleMutation.isPending}
                    title={device.notifications_enabled ? "Disable notifications" : "Enable notifications"}
                    style={{
                      background: device.notifications_enabled ? "rgba(68,136,255,0.12)" : "transparent",
                      border: `1px solid ${device.notifications_enabled ? "rgba(68,136,255,0.4)" : "var(--color-border)"}`,
                      borderRadius: 6, padding: "8px 12px", cursor: "pointer",
                      color: device.notifications_enabled ? "var(--color-blue)" : "var(--color-muted)",
                    }}
                  >
                    <FontAwesomeIcon icon={device.notifications_enabled ? faBell : faBellSlash} />
                  </button>
                  <Link
                    to={`/devices/${device.id}`}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "8px 12px", borderRadius: 6,
                      border: "1px solid var(--color-border)",
                      color: "var(--color-muted)", textDecoration: "none",
                      fontSize: 12, fontFamily: "var(--font-mono)",
                    }}
                  >
                    History <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 10 }} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
